import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp} from '@utils';
import {metaInfo} from '@configs';
import {TALENT_BRIDGE} from '@constants';
import loadSEO from '@helpers/loadSEO';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import dynamic from 'next/dynamic';
import {InjectSearchOverlay} from '@components';
import {AppState} from '@jobseeker/common-core/types';
import {isCSR} from '@jobseeker/lotus/utils';
import type {GetServerSidePropsContext} from 'next/types';
import TalentBridgeDetail from '@components/TalentBridge/TalentBridgeDetail';
import {fetchTalentBridgeAllLocations} from '@components/TalentBridge/utils';

const HiristAuthentication = dynamic(
  () =>
    import(
      '@jobseeker/client-harness/components/HiristAuthenticationHarness/AuthForm'
    ),
  {
    ssr: false,
  }
);
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

type TalentBridgeDetailProps = {
  seoConfig: object;
  isMobile?: boolean;
  city?: string;
};

const TalentBridgeDetailPage = ({seoConfig, city}: TalentBridgeDetailProps) => {
  const metaData = getConfigByApp(metaInfo, TALENT_BRIDGE, true, TALENT_BRIDGE);

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);

  const {searchOverlay, enableLoginPopup} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );
  const isSearchEnabled = useSelector(() => searchOverlay, shallowEqual);

  const onLoginSuccess = () => {
    if (isCSR()) {
      window.location.reload();
    }
  };

  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout seoConfig={seoConfig} />
      <NavLayout>
        <ResponsiveLayout
          breadcrumbElements={[]}
          variant={VersionVariants.VERSION_TWO}
        >
          {!isAppIimjobs && (
            <HiristAuthentication
              variant={VersionVariants.VERSION_TWO}
              onSuccess={onLoginSuccess}
              openDialog={enableLoginPopup}
              setOpenDialog={() => {
                dispatch({
                  type: 'AppContext/set_login_popup',
                  payload: false,
                });
              }}
            />
          )}
          <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
          <TalentBridgeDetail city={city} />
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    if (isAppIimjobs) {
      return {
        redirect: {
          permanent: false,
          destination: '/404',
        },
      };
    }

    // Extract and validate city slug from URL; if unsupported, return 404
    let normalizedCity = '';
    try {
      const rawPath = context?.resolvedUrl?.split('?')[0] || '';
      const afterBase = rawPath.split('/talent-bridge-detail/')[1] || '';
      const firstSeg = afterBase.split('/')[0] || '';
      normalizedCity = firstSeg
        .toLowerCase()
        .replace(/[^a-z-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
      const locations = await fetchTalentBridgeAllLocations();
      const supportedCities = Object.keys(locations || {});
      if (!normalizedCity || !supportedCities.includes(normalizedCity)) {
        return {
          notFound: true,
        };
      }
    } catch (e) {
      return {
        notFound: true,
      };
    }

    const seoConfig = await loadSEO(TALENT_BRIDGE);

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: TALENT_BRIDGE},
    });

    return {
      props: {
        seoConfig,
        city: normalizedCity,
      },
    };
  }
);

export default TalentBridgeDetailPage;
